import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { importProductsFunc, resetImportState } from "../../../store/custom-actions/products-actions/ImportProductsSlicer";
import { exportTemplateFunc } from "../../../store/custom-actions/products-actions/ExportProductsSlicer";
import { getProductsFunc } from "../../../store/custom-actions/products-actions/GetProductsSlicer";
import Swal from "sweetalert2";

/**
 * Import Products Modal Component
 * Allows users to import products from Excel file
 */
export default function ImportProductsModal({ show, onHide, onSuccess }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux selectors
  const { loading, success, error, imported, updated, skipped, errors } = useSelector(
    (state) => state.importProductsSlicer
  );
  const { templateLoading } = useSelector(
    (state) => state.exportProductsSlicer
  );

  // Local state
  const [selectedFile, setSelectedFile] = useState(null);
  const [locale, setLocale] = useState("en");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [fileError, setFileError] = useState("");

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      dispatch(resetImportState());
      setSelectedFile(null);
      setFileError("");
    }
  }, [show, dispatch]);

  // Handle success
  useEffect(() => {
    if (success && show) {
      const message = t("Products imported successfully. Imported: {{imported}}, Updated: {{updated}}, Skipped: {{skipped}}", {
        imported,
        updated,
        skipped,
      });

      Swal.fire({
        icon: "success",
        title: t("Import Successful"),
        html: `
          <div class="text-start">
            <p>${message}</p>
            ${errors.length > 0 ? `
              <div class="mt-3">
                <strong>${t("Errors")}:</strong>
                <ul class="text-danger mt-2" style="max-height: 200px; overflow-y: auto;">
                  ${errors.map((err, idx) => `<li>${err}</li>`).join("")}
                </ul>
              </div>
            ` : ""}
          </div>
        `,
        confirmButtonText: t("OK"),
      }).then(() => {
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
        // Refresh products list
        dispatch(getProductsFunc(""));
      });
    }
  }, [success, imported, updated, skipped, errors, show, t, dispatch, onSuccess]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError("");

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];
    
    if (!validTypes.includes(file.type)) {
      setFileError(t("Please select a valid Excel file (.xlsx or .xls)"));
      setSelectedFile(null);
      e.target.value = "";
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setFileError(t("File size must be less than 10MB"));
      setSelectedFile(null);
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  // Handle download template
  const handleDownloadTemplate = async () => {
    try {
      await dispatch(exportTemplateFunc(locale)).unwrap();
    } catch (error) {
      console.error("Template download error:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setFileError(t("Please select a file"));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("locale", locale);
    formData.append("update_existing", updateExisting ? "1" : "0");

    try {
      await dispatch(importProductsFunc(formData)).unwrap();
    } catch (error) {
      console.error("Import error:", error);
      // Error is handled by Redux state
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      setSelectedFile(null);
      setFileError("");
      dispatch(resetImportState());
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-file-import me-2"></i>
          {t("Import Products")}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Instructions */}
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-start">
              <i className="fas fa-info-circle me-2 mt-1"></i>
              <div>
                <strong>{t("Instructions")}:</strong>
                <ul className="mb-0 mt-2">
                  <li>{t("Download the template file to see the required format")}</li>
                  <li>{t("Fill in your product data following the template structure")}</li>
                  <li><strong>{t("Note")}:</strong> {t("Products are identified by SKU only. If a product with the same SKU exists, it will be updated. Otherwise, a new product will be created.")}</li>
                  <li>{t("Upload the completed Excel file")}</li>
                  <li>{t("Maximum file size: 10MB")}</li>
                </ul>
              </div>
            </div>
          </Alert>

          {/* Download Template Button */}
          <div className="mb-4">
            <Button
              variant="outline-primary"
              onClick={handleDownloadTemplate}
              disabled={templateLoading}
              className="w-100"
            >
              {templateLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("Downloading...")}
                </>
              ) : (
                <>
                  <i className="fas fa-download me-2"></i>
                  {t("Download Template")}
                </>
              )}
            </Button>
          </div>

          {/* File Input */}
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>{t("Select Excel File")}</strong>
              <span className="text-danger"> *</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={loading}
            />
            {fileError && (
              <Form.Text className="text-danger d-block mt-1">
                <i className="fas fa-exclamation-circle me-1"></i>
                {fileError}
              </Form.Text>
            )}
            {selectedFile && (
              <Form.Text className="text-success d-block mt-1">
                <i className="fas fa-check-circle me-1"></i>
                {t("File selected")}: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Form.Text>
            )}
          </Form.Group>

          {/* Locale Selection */}
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>{t("Locale")}</strong>
            </Form.Label>
            <Form.Select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              disabled={loading}
            >
              <option value="en">{t("English")}</option>
              <option value="ar">{t("Arabic")}</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {t("Select the language for product translations")}
            </Form.Text>
          </Form.Group>

          {/* Update Existing Checkbox */}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="update-existing"
              label={t("Update existing products if found")}
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
              disabled={loading}
            />
            <Form.Text className="text-muted d-block">
              {t("If checked, products with matching SKU will be updated instead of creating new ones. SKU is the unique identifier for products.")}
            </Form.Text>
          </Form.Group>

          {/* Error Display */}
          {error && !fileError && (
            <Alert variant="danger" className="mt-3">
              <div className="d-flex align-items-start">
                <i className="fas fa-exclamation-triangle me-2 mt-1"></i>
                <div>
                  <strong>{t("Import Failed")}</strong>
                  {error.message && <p className="mb-0 mt-1">{error.message}</p>}
                  {error.errors && Array.isArray(error.errors) && error.errors.length > 0 && (
                    <ul className="mb-0 mt-2">
                      {error.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !selectedFile || templateLoading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Importing...")}
              </>
            ) : (
              <>
                <i className="fas fa-upload me-2"></i>
                {t("Import Products")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
